import { create } from 'zustand';

export type ToastVariant = 'info' | 'success' | 'warning' | 'error' | 'vip';

export interface Toast {
  id: string;
  variant: ToastVariant;
  title: string;
  description?: string;
  /** Auto-dismiss after N ms. 0 = sticky (manual close only). */
  durationMs?: number;
  /** Cluster T-5: optional click handler on the toast body (e.g. chat message
   *  toasts deep-link into the thread). When set the card becomes clickable
   *  and shows a subtle pointer cursor. The dismiss X button keeps its own
   *  handler — clicking the X never fires onClick. */
  onClick?: () => void;
}

interface ToastState {
  toasts: Toast[];
  push: (t: Omit<Toast, 'id'>) => string;
  dismiss: (id: string) => void;
  clear: () => void;
}

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  push: (t) => {
    const id = `t-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    set((s) => ({ toasts: [...s.toasts, { id, durationMs: 5000, ...t }] }));
    return id;
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
  clear: () => set({ toasts: [] }),
}));

/** Type guard helper for callers building chat-clickable toasts. */
export const pushToastWithClick = (
  t: Omit<Toast, 'id'> & { onClick: () => void },
): string => useToastStore.getState().push(t);

/** Tiny convenience helpers. */
export const toast = {
  info: (title: string, description?: string) =>
    useToastStore.getState().push({ variant: 'info', title, description }),
  success: (title: string, description?: string) =>
    useToastStore.getState().push({ variant: 'success', title, description }),
  warning: (title: string, description?: string) =>
    useToastStore.getState().push({ variant: 'warning', title, description }),
  error: (title: string, description?: string) =>
    useToastStore.getState().push({ variant: 'error', title, description, durationMs: 8000 }),
  vip: (title: string, description?: string) =>
    useToastStore.getState().push({ variant: 'vip', title, description, durationMs: 30000 }),
};
